import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { schemaBuilderService } from "@/services/schemaBuilderService";
import { SchemaField, SchemaTemplate } from "@/types/schemaBuilder";
import React, { useState } from "react";
import { useAuth } from "../../../supabase/auth";
import { useOrganization } from "../../App";
import FieldEditor from "./FieldEditor";
import FormPreview from "./FormPreview";
import SchemaPreview from "./SchemaPreview";

interface SchemaBuilderProps {
  initialTemplate?: SchemaTemplate;
  onSave?: (template: SchemaTemplate) => void;
}

const SchemaBuilder: React.FC<SchemaBuilderProps> = ({
  initialTemplate,
  onSave,
}) => {
  const [schema, setSchema] = useState<Record<string, SchemaField>>(
    initialTemplate?.schema.properties || {}
  );
  const [activeTab, setActiveTab] = useState("editor");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState(initialTemplate?.name || "");
  const [templateDescription, setTemplateDescription] = useState(
    initialTemplate?.description || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth(); // Get the authenticated user
  const { currentOrganization } = useOrganization();

  const handleSchemaUpdate = (newSchema: Record<string, SchemaField>) => {
    setSchema(newSchema);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;

    setIsSaving(true);
    setError(null);

    try {
      // Prepare the required fields for the template
      const requiredFields = Object.entries(schema)
        .filter(([_, field]) => field.required)
        .map(([key]) => key);

      // Create the template data object
      const templateData: Omit<
        SchemaTemplate,
        "id" | "createdAt" | "updatedAt"
      > = {
        name: templateName,
        description: templateDescription,
        schema: {
          type: "object",
          properties: schema,
          required: requiredFields,
        },
        organizationId: currentOrganization?.id,
        createdBy: user?.id,
      };

      let savedTemplate: SchemaTemplate;

      if (initialTemplate?.id) {
        // Update existing template
        savedTemplate = await schemaBuilderService.updateTemplate(
          initialTemplate.id,
          templateData
        );
        console.log("Template updated:", savedTemplate);
      } else {
        // Create new template
        savedTemplate = await schemaBuilderService.createTemplate(templateData);
        console.log("Template created:", savedTemplate);
      }

      setSaveDialogOpen(false);

      if (onSave) {
        onSave(savedTemplate);
      }
    } catch (error) {
      console.error("Failed to save template:", error);
      setError(
        error instanceof Error ? error.message : "Unknown error occurred"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="text-2xl font-bold">
          {initialTemplate ? "Edit Template" : "Create Template"}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSaveDialogOpen(true)}>
            Save Template
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex-1 flex flex-col"
      >
        <div className="border-b px-4">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="editor">Field Editor</TabsTrigger>
            <TabsTrigger value="schema">JSON Schema</TabsTrigger>
            <TabsTrigger value="form">Form Preview</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 p-4 overflow-auto">
          <TabsContent value="editor" className="h-full">
            <FieldEditor schema={schema} onSchemaUpdate={handleSchemaUpdate} />
          </TabsContent>

          <TabsContent value="schema" className="h-full">
            <SchemaPreview schema={schema} />
          </TabsContent>

          <TabsContent value="form" className="h-full">
            <FormPreview schema={schema} />
          </TabsContent>
        </div>
      </Tabs>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Template</DialogTitle>
            <DialogDescription>
              Give your template a name and description to save it for future
              use.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-description">
                Description (optional)
              </Label>
              <Textarea
                id="template-description"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Enter template description"
                rows={3}
              />
            </div>

            {error && (
              <div className="bg-danger-bg text-danger p-3 rounded-md text-sm">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={!templateName.trim() || isSaving}
            >
              {isSaving ? "Saving..." : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchemaBuilder;
